import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ChevronDown, FileSearch, Globe, Info, Loader2, Save, ScanLine, ShieldAlert, ShieldCheck, XCircle, type LucideIcon } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import type { FindingStatus, FindingVerdict, QuarantineEntry, ScanEngineRun, ScanSchedule, SecurityFinding, SecurityOverview, SecurityScanRecord, WebsiteDetail, WebsiteSecurity } from "@hostdock/types";
import { cn, ConfirmDialog, Field, formatDateTime, formatRelative, FormError, Notice, Select, Skeleton, Textarea, toast } from "@hostdock/ui";
import { api } from "../api";
import { plural, scanResultLabel, threats, toReview } from "../lib/scan-result";
import { Cell, Pill, primaryButtonClass, SectionCard, StatCard, tableCell, TableShell } from "../components/page-kit";

const scanActive = (s: string) => s === "PENDING" || s === "RUNNING";
const finished = (s: string) => s === "COMPLETED" || s === "PARTIAL";
const engineLabel = (e: ScanEngineRun["engine"]) => (e === "clamav" ? "ClamAV" : "DockShield");


export function WebsiteSecurity({ w }: { w: WebsiteDetail }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["security", w.id], queryFn: () => api.security.get(w.id), refetchInterval: (s) => (s.state.data?.scans.some((x) => scanActive(x.status)) ? 3000 : 30_000) });
  const d = q.data;
  const latest = d?.scans[0];
  const mode = d?.ip.ipMode;
  return (
    <div className="flex flex-col">
      {d && (
        <div className="grid grid-cols-2 gap-[11px] xl:grid-cols-4">
          <StatCard icon={Globe} tile="teal" label="IP access" value={mode === "allow" ? "Allow-list" : mode === "deny" ? "Block-list" : "Open"} sub={mode === "off" ? "Everyone can visit" : `${d.ip.ipRules.length} rule${d.ip.ipRules.length === 1 ? "" : "s"}`} />
          <StatCard icon={ScanLine} tile="blue" label="Scanner" value={d.scanAvailable ? "Ready" : "Off"} sub={enginesSub(d.engines)} />
          <StatCard icon={latest && finished(latest.status) && threats(latest) ? ShieldAlert : latest && finished(latest.status) ? ShieldCheck : ScanLine} tile={latest && finished(latest.status) && (threats(latest) || toReview(latest)) ? "orange" : latest && finished(latest.status) ? "green" : "teal"} label="Latest scan"
            value={!latest ? "Never" : scanActive(latest.status) ? "Running" : latest.status === "FAILED" ? "Failed" : scanResultLabel(latest).label}
            sub={latest ? formatRelative(latest.createdAt) : "Run a scan below"} />
          <StatCard icon={FileSearch} tile="orange" label="Files scanned" value={latest?.filesScanned ?? "—"} sub={latest?.filesScanned != null ? "In the latest scan" : "Not reported yet"} />
        </div>
      )}
      <div className="grid items-start gap-x-[15px] xl:grid-cols-2">
        <IpRules websiteId={w.id} value={d?.ip} loading={q.isPending} onSaved={() => void qc.invalidateQueries({ queryKey: ["security", w.id] })} />
        <MalwareScan websiteId={w.id} available={d?.scanAvailable ?? false} scans={d?.scans} loading={q.isPending} onChange={() => void q.refetch()}
          schedule={d ? { value: d.scanSchedule, nextDueAt: d.nextScanDueAt } : undefined} />
      </div>
      {d && d.findings.length > 0 && <Findings websiteId={w.id} findings={d.findings} canQuarantine={d.quarantineAvailable} onChange={() => void qc.invalidateQueries({ queryKey: ["security", w.id] })} />}
      {d && d.quarantine.length > 0 && <Quarantine websiteId={w.id} entries={d.quarantine} onChange={() => void qc.invalidateQueries({ queryKey: ["security", w.id] })} />}
      {d && d.scans.length > 0 && <ScanHistory scans={d.scans} />}
    </div>
  );
}

function enginesSub(e: SecurityOverview["engines"]) {
  if (e.clamav && e.dockshield) return "ClamAV + DockShield";
  if (e.dockshield) return "DockShield (ClamAV not installed)";
  if (e.clamav) return "ClamAV (DockShield needs an agent update)";
  return "Not on this server yet";
}

function IpRules({ websiteId, value, loading, onSaved }: { websiteId: string; value?: WebsiteSecurity; loading: boolean; onSaved(): void }) {
  const [mode, setMode] = useState<WebsiteSecurity["ipMode"]>("off");
  const [text, setText] = useState("");
  useEffect(() => { if (value) { setMode(value.ipMode); setText(value.ipRules.join("\n")); } }, [value]);
  const rules = text.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
  const save = useMutation({ meta: { inlineError: true },
    mutationFn: () => api.security.setIp(websiteId, { ipMode: mode, ipRules: mode === "off" ? [] : rules }),
    onSuccess: () => { toast.success("IP rules updated — applying to the server"); onSaved(); },
  });
  return (
    <SectionCard title="IP access rules" description="Allow only certain visitors, or block specific ones, at the web server. Changes apply within a few seconds.">
      <div className="space-y-3 py-[15px]">
        <Field label="Mode">
          {(p) => <Select {...p} value={mode} onChange={(e) => setMode(e.target.value as WebsiteSecurity["ipMode"])} disabled={loading}>
            <option value="off">Open to everyone</option>
            <option value="allow">Allow only these IPs (block everyone else)</option>
            <option value="deny">Block these IPs</option>
          </Select>}
        </Field>
        {mode !== "off" && (
          <Field label={mode === "allow" ? "Allowed IP addresses / ranges" : "Blocked IP addresses / ranges"} hint="One per line. Use an address (203.0.113.4) or a CIDR range (198.51.100.0/24). IPv4 and IPv6.">
            {(p) => <Textarea {...p} rows={5} value={text} onChange={(e) => setText(e.target.value)} spellCheck={false} placeholder={"203.0.113.4\n198.51.100.0/24"} />}
          </Field>
        )}
        {mode === "allow" && <Notice tone="warning">With an allow-list, anyone not on the list — including you from a different network — is blocked. Include your own IP.</Notice>}
        <FormError error={save.error} />
        <div className="flex justify-end border-t border-line pt-3">
          <button type="button" className={primaryButtonClass} disabled={save.isPending || (mode !== "off" && rules.length === 0)} onClick={() => save.mutate()}>
            {save.isPending ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}Save rules
          </button>
        </div>
      </div>
    </SectionCard>
  );
}

function MalwareScan({ websiteId, available, scans, loading, onChange, schedule }: { websiteId: string; available: boolean; scans?: SecurityScanRecord[]; loading: boolean; onChange(): void; schedule?: { value: ScanSchedule; nextDueAt: string | null } }) {
  const setSchedule = useMutation({ meta: { inlineError: true }, mutationFn: (v: ScanSchedule) => api.security.setSchedule(websiteId, v), onSuccess: () => { toast.success("Automatic scans updated"); onChange(); } });
  const scan = useMutation({ meta: { inlineError: true }, mutationFn: () => api.security.scan(websiteId), onSuccess: () => { toast.success("Scan started"); onChange(); } });
  const latest = scans?.[0];
  const running = scans?.some((s) => scanActive(s.status));
  const t = threats(latest), r = toReview(latest);
  return (
    <SectionCard
      title="Malware scan"
      description="Checks your website's files with ClamAV (known malware) and DockShield (HostDock's checks for web shells, back doors, injected scripts and other signs of a hack). Nothing is deleted automatically."
      action={
        <button type="button" className={primaryButtonClass} disabled={scan.isPending || !available || running} onClick={() => scan.mutate()}>
          {scan.isPending || running ? <Loader2 size={16} className="animate-spin" /> : <ScanLine size={16} />}{running ? "Scanning…" : "Scan now"}
        </button>
      }
    >
      <div className="flex flex-col gap-3 py-[15px]">
        {loading ? <Skeleton className="h-20 w-full rounded-xl" />
          : !available ? <Note tone="info" icon={Info} title="Not available on your server yet">Malware scanning is set up automatically. Contact support if it stays unavailable.</Note>
          : !latest ? <Note tone="info" icon={ScanLine} title="No scans yet">Run a scan to check your files for malware.</Note>
          : scanActive(latest.status) ? <Note tone="info" icon={Loader2} title="Scanning your files">Started {formatRelative(latest.createdAt)}. This page updates on its own.</Note>
          : latest.status === "FAILED" ? <Note tone="bad" icon={XCircle} title="The last scan failed">{latest.error ?? "Try again in a few minutes."}</Note>
          : t ? <Note tone="bad" icon={ShieldAlert} title={`${plural(t, "threat")} found`}>{r ? `Plus ${plural(r, "suspicious file")} to review. ` : ""}See each file below with the reason it was flagged. Restoring a clean backup is often the safest fix.</Note>
          : r ? <Note tone="warn" icon={AlertTriangle} title={`${plural(r, "suspicious file")} to review`}>These matched a warning sign but may be legitimate. Look at each file below and mark it as a false positive if it is yours.</Note>
          : <Note tone="ok" icon={ShieldCheck} title="No malware found">{latest.filesScanned != null ? `${latest.filesScanned} files scanned` : "Scan complete"} on {formatDateTime(latest.completedAt)}.</Note>}
        {latest && latest.status === "PARTIAL" && <Notice tone="warning">Not every scanner finished: {latest.error ?? "one engine failed"}. The results below come from the scanners that did.</Notice>}
        {latest && finished(latest.status) && <EngineList engines={latest.engines} />}
        {available && schedule && (
          <div className="flex flex-wrap items-end gap-3 border-t border-line pt-3">
            <Field label="Automatic scans">
              {(p) => <Select {...p} value={schedule.value} disabled={setSchedule.isPending} onChange={(e) => setSchedule.mutate(e.target.value as ScanSchedule)}>
                <option value="weekly">Weekly</option>
                <option value="daily">Daily</option>
                <option value="off">Off</option>
              </Select>}
            </Field>
            <span className="pb-2 text-[12px] text-muted">
              {schedule.value === "off" ? "Only when you click Scan now."
                : schedule.nextDueAt ? `Next automatic scan: ${new Date(schedule.nextDueAt) <= new Date() ? "soon" : `after ${formatDateTime(schedule.nextDueAt)}`}. Scans run at low priority, one website per server at a time.` : ""}
            </span>
          </div>
        )}
        <FormError error={scan.error ?? setSchedule.error} />
      </div>
    </SectionCard>
  );
}

function EngineList({ engines }: { engines: ScanEngineRun[] }) {
  return (
    <ul className="flex flex-col gap-1 text-[12px] text-muted">
      {engines.map((e) => (
        <li key={e.engine} className="flex flex-wrap items-center gap-2">
          <Pill tone={e.status === "COMPLETED" ? "ok" : e.status === "FAILED" ? "bad" : "off"} dot>{engineLabel(e.engine)}</Pill>
          <span>
            {e.status === "COMPLETED"
              ? [e.version && `${e.engine === "clamav" ? "engine" : "rules"} ${e.version}`, e.filesExamined != null && `${e.filesExamined} files checked`,
                e.skipped && e.skipped.tooLarge + e.skipped.unreadable > 0 && `${e.skipped.tooLarge + e.skipped.unreadable} skipped (too large or unreadable)`, e.truncated && "more files matched than shown"].filter(Boolean).join(" · ")
              : e.error}
          </span>
        </li>
      ))}
    </ul>
  );
}

const VERDICT: Record<FindingVerdict, { label: string; tone: "bad" | "wait" }> = {
  MALICIOUS: { label: "Malware", tone: "bad" },
  LIKELY_MALICIOUS: { label: "Likely malicious", tone: "bad" },
  SUSPICIOUS: { label: "Suspicious", tone: "wait" },
};
const STATUS: Record<FindingStatus, string> = { OPEN: "Needs action", FALSE_POSITIVE: "Marked as false positive", RESOLVED: "Marked as resolved" };

function Findings({ websiteId, findings, canQuarantine, onChange }: { websiteId: string; findings: SecurityFinding[]; canQuarantine: boolean; onChange(): void }) {
  return (
    <SectionCard title="Flagged files" count={findings.length} description="Each file the latest scan flagged, why, and what the scanners saw. Marking a file does not change it on the server; quarantine moves it out of your website, and you can restore it.">
      <ul className="divide-y divide-line">
        {findings.map((f) => <FindingRow key={f.id} websiteId={websiteId} f={f} canQuarantine={canQuarantine} onChange={onChange} />)}
      </ul>
    </SectionCard>
  );
}

function FindingRow({ websiteId, f, canQuarantine, onChange }: { websiteId: string; f: SecurityFinding; canQuarantine: boolean; onChange(): void }) {
  const [open, setOpen] = useState(false);
  const [confirmQ, setConfirmQ] = useState(false);
  const set = useMutation({
    mutationFn: (status: FindingStatus) => api.security.setFindingStatus(websiteId, f.id, status),
    onSuccess: (x) => { toast.success(x.status === "OPEN" ? "Finding reopened" : STATUS[x.status]); onChange(); },
  });
  const quarantine = useMutation({
    mutationFn: () => api.security.quarantine(websiteId, f.id),
    onSuccess: () => { setConfirmQ(false); toast.success("File moved to quarantine"); onChange(); },
  });
  const restore = useMutation({
    mutationFn: (entryId: string) => api.security.restore(websiteId, entryId),
    onSuccess: () => { toast.success("File restored to your website"); onChange(); },
  });
  const inQuarantine = f.quarantine?.status === "QUARANTINED";
  const v = VERDICT[f.verdict];
  const dimmed = f.status !== "OPEN";
  return (
    <li className={cn("flex flex-col gap-2 py-3", dimmed && "opacity-70")}>
      <div className="flex flex-wrap items-center gap-2">
        <Pill tone={dimmed ? "off" : v.tone} dot>{v.label}</Pill>
        <span className="min-w-0 flex-1 font-mono text-[12px] break-all">{f.path}</span>
        <span className="text-[12px] text-muted">{f.severity.toLowerCase()} severity · {f.confidence.toLowerCase()} confidence</span>
      </div>
      <p className="text-[12.5px] font-medium text-fg">{f.title}</p>
      <p className="text-[12.5px] text-muted">{f.explanation}</p>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className="inline-flex items-center gap-1 text-[12.5px] font-medium text-accent" onClick={() => setOpen((x) => !x)} aria-expanded={open}>
          <ChevronDown size={14} className={cn("transition-transform", open && "rotate-180")} />{open ? "Hide evidence" : `Show evidence (${f.evidence.length})`}
        </button>
        <span className="flex-1" />
        {inQuarantine ? (
          <>
            <Pill tone="info" dot>In quarantine</Pill>
            <button type="button" className="rounded-lg border border-line px-[10px] py-[5px] text-[12.5px] hover:bg-surface-2" disabled={restore.isPending} onClick={() => restore.mutate(f.quarantine!.id)}>Restore</button>
          </>
        ) : f.status !== "OPEN" && <span className="text-[12px] text-muted">{STATUS[f.status]}</span>}
        {inQuarantine ? null : f.status === "OPEN" ? (
          <>
            {canQuarantine && f.sha256 && <button type="button" className="rounded-lg border border-danger/40 px-[10px] py-[5px] text-[12.5px] font-medium text-danger hover:bg-danger-soft" onClick={() => setConfirmQ(true)}>Quarantine</button>}
            <button type="button" className="rounded-lg border border-line px-[10px] py-[5px] text-[12.5px] hover:bg-surface-2" disabled={set.isPending} onClick={() => set.mutate("FALSE_POSITIVE")}>Not malware</button>
            <button type="button" className="rounded-lg border border-line px-[10px] py-[5px] text-[12.5px] hover:bg-surface-2" disabled={set.isPending} onClick={() => set.mutate("RESOLVED")}>I fixed it</button>
          </>
        ) : (
          <button type="button" className="rounded-lg border border-line px-[10px] py-[5px] text-[12.5px] hover:bg-surface-2" disabled={set.isPending} onClick={() => set.mutate("OPEN")}>Reopen</button>
        )}
      </div>
      {open && (
        <ul className="flex flex-col gap-2 rounded-xl border border-line p-[12px] text-[12px]">
          {f.evidence.map((e, i) => (
            <li key={i} className="flex flex-col gap-1">
              <span className="font-medium text-fg">{engineLabel(e.engine)} · {e.ruleId}{e.ruleVersion ? ` v${e.ruleVersion}` : ""} · {e.strength} evidence{e.line ? ` · line ${e.line}` : ""}</span>
              {e.engine !== "clamav" && <span className="text-muted">{e.description}</span>}
              {e.excerpt && <code className="block rounded-md bg-surface-2 px-2 py-1 font-mono text-[11.5px] break-all">{e.excerpt}</code>}
            </li>
          ))}
          {f.sha256 && <li className="font-mono text-[11px] break-all text-subtle">SHA-256 {f.sha256}</li>}
        </ul>
      )}
      <FormError error={set.error ?? restore.error} />
      <ConfirmDialog open={confirmQ} onOpenChange={setConfirmQ} tone="primary" title="Move this file to quarantine?" confirmLabel="Quarantine file"
        description={f.path} loading={quarantine.isPending} error={quarantine.error} onConfirm={() => quarantine.mutate()}>
        <p className="text-[13px] text-muted">The file is removed from your website and kept on the server where nothing can run it. You can restore it unchanged at any time. If your site needs this file, that part of the site stops working until you restore it.</p>
      </ConfirmDialog>
    </li>
  );
}

function Quarantine({ websiteId, entries, onChange }: { websiteId: string; entries: QuarantineEntry[]; onChange(): void }) {
  const [removing, setRemoving] = useState<QuarantineEntry | null>(null);
  const restore = useMutation({ mutationFn: (id: string) => api.security.restore(websiteId, id), onSuccess: () => { toast.success("File restored to your website"); onChange(); } });
  const del = useMutation({
    mutationFn: (e: QuarantineEntry) => api.security.deleteQuarantined(websiteId, e.id, e.path),
    onSuccess: () => { setRemoving(null); toast.success("Quarantined copy deleted"); onChange(); },
  });
  return (
    <SectionCard title="Quarantine" count={entries.length} description="Files moved out of your website. They are kept on the server, where nothing can run them, until you restore or delete them.">
      <ul className="divide-y divide-line">
        {entries.map((e) => (
          <li key={e.id} className="flex flex-wrap items-center gap-2 py-3 text-[12.5px]">
            <span className="min-w-0 flex-1 font-mono text-[12px] break-all">{e.path}</span>
            <span className="text-muted" title={formatDateTime(e.createdAt)}>{formatRelative(e.createdAt)}</span>
            <button type="button" className="rounded-lg border border-line px-[10px] py-[5px] hover:bg-surface-2" disabled={restore.isPending} onClick={() => restore.mutate(e.id)}>Restore</button>
            <button type="button" className="rounded-lg border border-danger/40 px-[10px] py-[5px] text-danger hover:bg-danger-soft" onClick={() => setRemoving(e)}>Delete permanently</button>
          </li>
        ))}
      </ul>
      <FormError error={restore.error} />
      <ConfirmDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)} title="Delete the quarantined copy?" confirmLabel="Delete permanently"
        description="This cannot be undone. Type the file's path to confirm." confirmText={removing?.path} loading={del.isPending} error={del.error} onConfirm={() => removing && del.mutate(removing)} />
    </SectionCard>
  );
}

function Note({ tone, icon: Icon, title, children }: { tone: "ok" | "bad" | "warn" | "info"; icon: LucideIcon; title: string; children: ReactNode }) {
  const box = { ok: "border-[#c8efd9] bg-[#f0fbf5] dark:border-success/30 dark:bg-success-soft", bad: "border-danger/30 bg-danger-soft", warn: "border-warning/30 bg-warning-soft", info: "border-[#cdeef2] bg-[#effafb] dark:border-accent/30 dark:bg-accent-soft" }[tone];
  const ic = { ok: "text-[#14a04a]", bad: "text-danger", warn: "text-warning", info: "text-[#0a8ea0] dark:text-accent" }[tone];
  return (
    <div className={cn("flex items-start gap-3 rounded-xl border p-[14px] text-[12.5px]", box)} role="status">
      <Icon size={20} className={cn("shrink-0", ic, Icon === Loader2 && "animate-spin")} />
      <div className="min-w-0"><p className="text-[13px] font-semibold text-fg">{title}</p><p className="mt-1 text-muted">{children}</p></div>
    </div>
  );
}

function ScanHistory({ scans }: { scans: SecurityScanRecord[] }) {
  const cols = "xl:grid xl:grid-cols-[220px_170px_150px_minmax(0,1fr)] xl:items-center";
  return (
    <SectionCard title="Scan history" count={scans.length} description="Every scan of this website, newest first.">
      <TableShell flat label="Scan history" cols={cols} headers={["Started", "Result", "Files scanned", "Details"]}>
        {scans.map((x) => (
          <li key={x.id} role="row" className={cn(cols, "flex flex-col gap-2 py-3 text-[12.5px] text-fg xl:min-h-[54px] xl:gap-0 xl:py-[4px]")}>
            <div role="cell" className="flex items-center gap-[10px] xl:pr-3"><ScanLine size={15} className="shrink-0 text-subtle" /><span className="font-medium" title={formatDateTime(x.createdAt)}>{formatRelative(x.createdAt)}</span></div>
            <div className="grid grid-cols-2 gap-2 xl:contents">
              <Cell label="Result" className={cn(tableCell, "xl:h-[40px]")}>
                <Pill tone={finished(x.status) ? scanResultLabel(x).tone : x.status === "FAILED" ? "bad" : "info"} dot>
                  {finished(x.status) ? scanResultLabel(x).label : x.status === "FAILED" ? "Failed" : "Running"}
                </Pill>
              </Cell>
              <Cell label="Files scanned" className={cn(tableCell, "xl:h-[40px]")}><span className="tabular-nums">{x.filesScanned ?? (finished(x.status) ? "Unavailable" : "—")}</span></Cell>
            </div>
            <Cell label="Details" className={cn(tableCell, "xl:h-[40px]")}>
              <span className={cn("max-w-full truncate", x.error ? "text-danger" : "text-muted")} title={x.error ?? undefined}>{x.error ?? (x.engines.filter((e) => e.status === "COMPLETED").map((e) => `${engineLabel(e.engine)}${e.version ? ` ${e.version}` : ""}`).join(" · ") || "—")}</span>
            </Cell>
          </li>
        ))}
      </TableShell>
    </SectionCard>
  );
}
